import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Calendar,
  Plus,
  Loader2,
  Lock,
  Unlock,
  Eye,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSessionState } from '@/hooks/use-session-state';
import { trpc } from '@/lib/trpc';
import { cn, formatCurrency } from '@/lib/utils';

type PeriodType = 'MONTH' | 'QUARTER' | 'YEAR';
type PeriodStatus = 'OPEN' | 'CLOSED' | 'LOCKED';

export function TradingFiscalPeriodsPage() {
  const { t, i18n } = useTranslation('tradingAccounting');
  const { t: tCommon } = useTranslation('common');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
  const navigate = useNavigate();

  // Filters
  const [periodType, setPeriodType] = useSessionState<PeriodType | ''>(
    'trading:accounting-periods:periodType',
    ''
  );
  const [statusFilter, setStatusFilter] = useSessionState<PeriodStatus | ''>(
    'trading:accounting-periods:statusFilter',
    ''
  );
  const [yearFilter, setYearFilter] = useSessionState(
    'trading:accounting-periods:yearFilter',
    new Date().getFullYear()
  );
  const [page, setPage] = useState(1);

  // Modal states
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [generateYear, setGenerateYear] = useState(new Date().getFullYear());
  const [generateType, setGenerateType] = useState<PeriodType>('MONTH');
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [reopenModalOpen, setReopenModalOpen] = useState(false);
  const [lockModalOpen, setLockModalOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<{
    id: string;
    name: string;
    status: PeriodStatus;
  } | null>(null);
  const [reopenReason, setReopenReason] = useState('');
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Queries
  const { data, isLoading } = trpc.tradingFiscalPeriod.list.useQuery({
    page,
    periodType: periodType || undefined,
    status: statusFilter || undefined,
    year: yearFilter,
  });

  const { data: periodDetail, isLoading: detailLoading } =
    trpc.tradingFiscalPeriod.getSummary.useQuery(
      { id: selectedPeriod?.id ?? '' },
      { enabled: !!selectedPeriod && detailModalOpen }
    );

  const utils = trpc.useUtils();

  // Mutations
  const generateMutation = trpc.tradingFiscalPeriod.generateYear.useMutation({
    onSuccess: () => {
      utils.tradingFiscalPeriod.list.invalidate();
      setGenerateModalOpen(false);
    },
  });

  const closeMutation = trpc.tradingFiscalPeriod.close.useMutation({
    onSuccess: () => {
      utils.tradingFiscalPeriod.list.invalidate();
      setCloseModalOpen(false);
      setSelectedPeriod(null);
    },
  });

  const reopenMutation = trpc.tradingFiscalPeriod.reopen.useMutation({
    onSuccess: () => {
      utils.tradingFiscalPeriod.list.invalidate();
      setReopenModalOpen(false);
      setSelectedPeriod(null);
      setReopenReason('');
    },
  });

  const lockMutation = trpc.tradingFiscalPeriod.lock.useMutation({
    onSuccess: () => {
      utils.tradingFiscalPeriod.list.invalidate();
      setLockModalOpen(false);
      setSelectedPeriod(null);
    },
  });

  const periods = data?.periods ?? [];
  const pagination = data?.pagination ?? { page: 1, limit: 20, total: 0, totalPages: 0 };

  const statusColors: Record<PeriodStatus, string> = {
    OPEN: 'bg-green-100 text-green-700',
    CLOSED: 'bg-amber-100 text-amber-700',
    LOCKED: 'bg-red-100 text-red-700',
  };

  const statusIcons: Record<PeriodStatus, React.ReactNode> = {
    OPEN: <CheckCircle2 className="h-3.5 w-3.5" />,
    CLOSED: <XCircle className="h-3.5 w-3.5" />,
    LOCKED: <Lock className="h-3.5 w-3.5" />,
  };

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/trading/accounting')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{t('fiscalPeriods.title', 'Fiscal Periods')}</h1>
            <p className="text-muted-foreground">
              {t('fiscalPeriods.subtitle', 'Manage trading accounting periods')}
            </p>
          </div>
        </div>
        <Button className="h-8" onClick={() => setGenerateModalOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t('fiscalPeriods.generatePeriods', 'Generate Periods')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <select
          value={periodType}
          onChange={(e) => setPeriodType(e.target.value as PeriodType | '')}
          className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 border px-4 py-2 focus:outline-none focus:ring-1"
        >
          <option value="">{t('fiscalPeriods.filters.allTypes', 'All Types')}</option>
          <option value="MONTH">{t('fiscalPeriods.periodTypes.MONTH', 'Monthly')}</option>
          <option value="QUARTER">{t('fiscalPeriods.periodTypes.QUARTER', 'Quarterly')}</option>
          <option value="YEAR">{t('fiscalPeriods.periodTypes.YEAR', 'Yearly')}</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as PeriodStatus | '')}
          className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 border px-4 py-2 focus:outline-none focus:ring-1"
        >
          <option value="">{t('fiscalPeriods.filters.allStatuses', 'All Statuses')}</option>
          <option value="OPEN">{t('fiscalPeriods.status.OPEN', 'Open')}</option>
          <option value="CLOSED">{t('fiscalPeriods.status.CLOSED', 'Closed')}</option>
          <option value="LOCKED">{t('fiscalPeriods.status.LOCKED', 'Locked')}</option>
        </select>
        <select
          value={yearFilter}
          onChange={(e) => setYearFilter(parseInt(e.target.value))}
          className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 border px-4 py-2 focus:outline-none focus:ring-1"
        >
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>

      {/* Periods Table */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : periods.length === 0 ? (
        <div className="border-border bg-card border py-12 text-center">
          <Calendar className="text-muted-foreground/50 mx-auto h-12 w-12" />
          <h3 className="mt-4 text-lg font-medium">
            {t('fiscalPeriods.noPeriods', 'No periods found')}
          </h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {t('fiscalPeriods.noPeriodsHelp', 'Generate periods for a year to get started')}
          </p>
          <Button className="mt-4 h-8" onClick={() => setGenerateModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('fiscalPeriods.generatePeriods', 'Generate Periods')}
          </Button>
        </div>
      ) : (
        <div className="border-border bg-card overflow-hidden border">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  {t('fiscalPeriods.columns.name', 'Name')}
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  {t('fiscalPeriods.columns.type', 'Type')}
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  {t('fiscalPeriods.columns.startDate', 'Start Date')}
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  {t('fiscalPeriods.columns.endDate', 'End Date')}
                </th>
                <th className="px-4 py-3 text-center text-sm font-medium">
                  {t('fiscalPeriods.columns.status', 'Status')}
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium">
                  {t('fiscalPeriods.columns.actions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {periods.map((period) => (
                <tr key={period.id} className="hover:bg-muted/30">
                  <td className="px-4 py-3 font-medium">{period.name}</td>
                  <td className="px-4 py-3">
                    <span className="text-muted-foreground text-sm">
                      {t(`fiscalPeriods.periodTypes.${period.periodType}`, period.periodType)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {new Date(period.startDate).toLocaleDateString(dateLocale)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {new Date(period.endDate).toLocaleDateString(dateLocale)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium',
                        statusColors[period.status as PeriodStatus]
                      )}
                    >
                      {statusIcons[period.status as PeriodStatus]}
                      {t(`fiscalPeriods.status.${period.status}`, period.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedPeriod({
                            id: period.id,
                            name: period.name,
                            status: period.status as PeriodStatus,
                          });
                          setDetailModalOpen(true);
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {period.status === 'OPEN' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedPeriod({
                              id: period.id,
                              name: period.name,
                              status: period.status as PeriodStatus,
                            });
                            setCloseModalOpen(true);
                          }}
                        >
                          <XCircle className="mr-2 h-4 w-4" />
                          {t('fiscalPeriods.actions.close', 'Close')}
                        </Button>
                      )}
                      {period.status === 'CLOSED' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedPeriod({
                                id: period.id,
                                name: period.name,
                                status: period.status as PeriodStatus,
                              });
                              setReopenModalOpen(true);
                            }}
                          >
                            <Unlock className="mr-2 h-4 w-4" />
                            {t('fiscalPeriods.actions.reopen', 'Reopen')}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => {
                              setSelectedPeriod({
                                id: period.id,
                                name: period.name,
                                status: period.status as PeriodStatus,
                              });
                              setLockModalOpen(true);
                            }}
                          >
                            <Lock className="mr-2 h-4 w-4" />
                            {t('fiscalPeriods.actions.lock', 'Lock')}
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            {tCommon('previous', 'Previous')}
          </Button>
          <span className="px-4 py-2 text-sm">
            {tCommon('page', 'Page')} {page} / {pagination.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
            disabled={page >= pagination.totalPages}
          >
            {tCommon('next', 'Next')}
          </Button>
        </div>
      )}

      {/* Generate Modal */}
      {generateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background w-full max-w-md p-6 shadow-lg">
            <h2 className="mb-4 text-lg font-semibold">
              {t('fiscalPeriods.generatePeriods', 'Generate Periods')}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">
                  {t('fiscalPeriods.selectYear', 'Select Year')}
                </label>
                <select
                  value={generateYear}
                  onChange={(e) => setGenerateYear(parseInt(e.target.value))}
                  className="border-border bg-background focus:border-foreground focus:ring-foreground/20 mt-1 h-8 w-full border px-4 py-1 focus:outline-none focus:ring-1"
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">
                  {t('fiscalPeriods.periodType', 'Period Type')}
                </label>
                <select
                  value={generateType}
                  onChange={(e) => setGenerateType(e.target.value as PeriodType)}
                  className="border-border bg-background focus:border-foreground focus:ring-foreground/20 mt-1 h-8 w-full border px-4 py-1 focus:outline-none focus:ring-1"
                >
                  <option value="MONTH">{t('fiscalPeriods.periodTypes.MONTH', 'Monthly')}</option>
                  <option value="QUARTER">
                    {t('fiscalPeriods.periodTypes.QUARTER', 'Quarterly')}
                  </option>
                  <option value="YEAR">{t('fiscalPeriods.periodTypes.YEAR', 'Yearly')}</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" className="h-8" onClick={() => setGenerateModalOpen(false)}>
                {tCommon('cancel', 'Cancel')}
              </Button>
              <Button
                className="h-8"
                onClick={() =>
                  generateMutation.mutate({ year: generateYear, periodType: generateType })
                }
                disabled={generateMutation.isPending}
              >
                {generateMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="mr-2 h-4 w-4" />
                )}
                {t('fiscalPeriods.generatePeriods', 'Generate Periods')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Close Modal */}
      {closeModalOpen && selectedPeriod && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background w-full max-w-md p-6 shadow-lg">
            <div className="mb-4 flex items-center gap-3">
              <div className="bg-amber-100 p-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <h2 className="text-lg font-semibold">
                {t('fiscalPeriods.closeConfirm.title', 'Close Period')}
              </h2>
            </div>
            <p className="text-muted-foreground">
              {t(
                'fiscalPeriods.closeConfirm.message',
                'Are you sure you want to close "{{name}}"? No new entries can be posted to this period.',
                { name: selectedPeriod.name }
              )}
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" className="h-8" onClick={() => setCloseModalOpen(false)}>
                {tCommon('cancel', 'Cancel')}
              </Button>
              <Button
                className="h-8"
                onClick={() => closeMutation.mutate({ id: selectedPeriod.id })}
                disabled={closeMutation.isPending}
              >
                {closeMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <XCircle className="mr-2 h-4 w-4" />
                )}
                {t('fiscalPeriods.closeConfirm.confirm', 'Close Period')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reopen Modal */}
      {reopenModalOpen && selectedPeriod && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background w-full max-w-md p-6 shadow-lg">
            <h2 className="mb-4 text-lg font-semibold">
              {t('fiscalPeriods.reopenConfirm.title', 'Reopen Period')}
            </h2>
            <p className="text-muted-foreground mb-4">
              {t(
                'fiscalPeriods.reopenConfirm.message',
                'Are you sure you want to reopen "{{name}}"?',
                { name: selectedPeriod.name }
              )}
            </p>
            <div>
              <label className="text-sm font-medium">
                {t('fiscalPeriods.reopenConfirm.reason', 'Reason for reopening')}
              </label>
              <textarea
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                placeholder={t('fiscalPeriods.reopenConfirm.reasonPlaceholder', 'Enter reason...')}
                className="border-border bg-background mt-1 min-h-[80px] w-full border px-4 py-2"
              />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <Button
                variant="outline"
                className="h-8"
                onClick={() => {
                  setReopenModalOpen(false);
                  setReopenReason('');
                }}
              >
                {tCommon('cancel', 'Cancel')}
              </Button>
              <Button
                className="h-8"
                onClick={() =>
                  reopenMutation.mutate({ id: selectedPeriod.id, reason: reopenReason })
                }
                disabled={reopenMutation.isPending || !reopenReason.trim()}
              >
                {reopenMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Unlock className="mr-2 h-4 w-4" />
                )}
                {t('fiscalPeriods.reopenConfirm.confirm', 'Reopen Period')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Lock Modal */}
      {lockModalOpen && selectedPeriod && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background w-full max-w-md p-6 shadow-lg">
            <div className="mb-4 flex items-center gap-3">
              <div className="bg-red-100 p-2">
                <Lock className="h-5 w-5 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold">
                {t('fiscalPeriods.lockConfirm.title', 'Lock Period')}
              </h2>
            </div>
            <p className="text-muted-foreground">
              {t(
                'fiscalPeriods.lockConfirm.message',
                'Are you sure you want to permanently lock "{{name}}"? This cannot be undone.',
                { name: selectedPeriod.name }
              )}
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" className="h-8" onClick={() => setLockModalOpen(false)}>
                {tCommon('cancel', 'Cancel')}
              </Button>
              <Button
                variant="destructive"
                className="h-8"
                onClick={() => lockMutation.mutate({ id: selectedPeriod.id })}
                disabled={lockMutation.isPending}
              >
                {lockMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Lock className="mr-2 h-4 w-4" />
                )}
                {t('fiscalPeriods.lockConfirm.confirm', 'Lock Period')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detailModalOpen && selectedPeriod && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background w-full max-w-lg p-6 shadow-lg">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">{selectedPeriod.name}</h2>
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium',
                  statusColors[selectedPeriod.status]
                )}
              >
                {statusIcons[selectedPeriod.status]}
                {t(`fiscalPeriods.status.${selectedPeriod.status}`, selectedPeriod.status)}
              </span>
            </div>

            {detailLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="text-primary h-6 w-6 animate-spin" />
              </div>
            ) : periodDetail ? (
              <div className="space-y-4">
                <h3 className="text-muted-foreground text-sm font-medium">
                  {t('fiscalPeriods.detail.summary', 'Period Summary')}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="border-border border p-4">
                    <p className="text-muted-foreground text-sm">
                      {t('fiscalPeriods.detail.journalEntries', 'Journal Entries')}
                    </p>
                    <p className="mt-1 text-2xl font-bold">
                      {periodDetail.summary.journalEntries.totalCount}
                    </p>
                    <div className="mt-2 space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span>{t('fiscalPeriods.detail.posted', 'Posted')}</span>
                        <span>{periodDetail.summary.journalEntries.byStatus?.POSTED ?? 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t('fiscalPeriods.detail.draft', 'Draft')}</span>
                        <span>{periodDetail.summary.journalEntries.byStatus?.DRAFT ?? 0}</span>
                      </div>
                    </div>
                  </div>
                  <div className="border-border border p-4">
                    <p className="text-muted-foreground text-sm">
                      {t('fiscalPeriods.detail.totalDebits', 'Total Debits')}
                    </p>
                    <p className="mt-1 font-mono text-2xl font-bold">
                      {formatCurrency(periodDetail.summary.journalEntries.totalDebits, 'TRY')}
                    </p>
                    <p className="text-muted-foreground mt-2 text-xs">
                      {t('fiscalPeriods.detail.totalCredits', 'Total Credits')}:{' '}
                      {formatCurrency(periodDetail.summary.journalEntries.totalCredits, 'TRY')}
                    </p>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="mt-6 flex justify-end">
              <Button variant="outline" className="h-8" onClick={() => setDetailModalOpen(false)}>
                {tCommon('close', 'Close')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
